import {
  ColDef,
  EditableCallbackParams,
  CellClassParams,
  ValueFormatterParams,
  ICellRendererParams,
  ICellEditorParams,
} from 'ag-grid-community'
import { entitySearchReferenceWbsItemType } from '../../../../page-properties/bulksheet-properties/wbsItemAdditionalProperty'
import {
  isAdditionalPropertyRow,
  isGroupHeaderRow,
  AdditionalPropertyRow,
  WbsItemAdditionalPropertyRow,
} from '../../rowModel/wbsItemAdditionalPropertyRow'
import { DISABLE_COLOR } from './style'
import { SelectCellEditor } from '../../../../containers/BulkSheetView/components/cellEditor'
import { ClientSideSelectFilter } from '../../../../containers/BulkSheetView/components/filter'
import { intl } from '../../../../../i18n'
import store from '../../../../../store'
import {
  EntitySearchReferenceEntityType,
  EntitySearchReferenceEntityWbsItemType,
  getEntitySearchEntityWbsItemTypeLabel,
  isEntitySearchReferenceEntityWbsItemType,
  shouldHaveEntitySearchEntityWbsItemType,
} from '../../entitySearchReferenceType'
import { entitySearchReferenceEntityIsChangeable } from '../../../../../domain/entity/WbsItemAdditionalPropertyEntity'

export const entitySearchReferenceEntityWbsItemTypeColumnDef: ColDef = {
  ...entitySearchReferenceWbsItemType,
  editable: (params: EditableCallbackParams<WbsItemAdditionalPropertyRow>) => {
    if (!params.node?.data || isGroupHeaderRow(params.node.data)) {
      return false
    }
    const additionalPropertyRow = params.node.data as AdditionalPropertyRow
    return (
      isAdditionalPropertyRow(additionalPropertyRow) &&
      entitySearchReferenceEntityIsChangeable(additionalPropertyRow) &&
      shouldHaveEntitySearchEntityWbsItemType(
        additionalPropertyRow.propertyType,
        additionalPropertyRow.entitySearchReferenceEntityType
      )
    )
  },
  valueFormatter: (
    params: ValueFormatterParams<WbsItemAdditionalPropertyRow>
  ) => {
    if (!params.node?.data || isGroupHeaderRow(params.node.data)) {
      return ''
    }
    const additionalPropertyRow = params.node.data as AdditionalPropertyRow
    if (!additionalPropertyRow.entitySearchReferenceEntityWbsItemType) {
      return ''
    }
    if (
      EntitySearchReferenceEntityType.WBS_ITEM ===
        additionalPropertyRow.entitySearchReferenceEntityType &&
      isEntitySearchReferenceEntityWbsItemType(
        additionalPropertyRow.entitySearchReferenceEntityWbsItemType
      )
    ) {
      return getEntitySearchEntityWbsItemTypeLabel(
        additionalPropertyRow.entitySearchReferenceEntityWbsItemType
      )
    }
    const ticketTypes = store.getState().project.ticketTypes
    return (
      ticketTypes.find(
        v =>
          v.code ===
          additionalPropertyRow.entitySearchReferenceEntityWbsItemType
      )?.name || ''
    )
  },
  cellRendererParams: {
    validate: (
      value: string | undefined,
      params: ICellRendererParams<WbsItemAdditionalPropertyRow>
    ): string | undefined => {
      if (!params.node?.data || !isAdditionalPropertyRow(params.node.data)) {
        return undefined
      }
      const additionalPropertyRow = params.node.data as AdditionalPropertyRow
      if (
        shouldHaveEntitySearchEntityWbsItemType(
          additionalPropertyRow.propertyType,
          additionalPropertyRow.entitySearchReferenceEntityType
        )
      ) {
        if (!value) {
          return intl.formatMessage({
            id: 'wbsItemAdditionalProperties.validation.entitySearchReferenceEntityWbsItemType.required',
          })
        }
      }
      return undefined
    },
    tooltip: (params: ICellRendererParams<WbsItemAdditionalPropertyRow>) => {
      if (!params.node.data || !isAdditionalPropertyRow(params.node.data)) {
        return undefined
      }
      const additionalPropertyRow = params.node.data as AdditionalPropertyRow
      if (
        shouldHaveEntitySearchEntityWbsItemType(
          additionalPropertyRow.propertyType,
          additionalPropertyRow.entitySearchReferenceEntityType
        ) &&
        !entitySearchReferenceEntityIsChangeable(additionalPropertyRow)
      ) {
        return intl.formatMessage({
          id: 'wbsItemAdditionalProperties.tooltip.usingProperty.canot.change',
        })
      }
      return undefined
    },
  },
  cellEditor: SelectCellEditor,
  cellEditorParams: {
    options: (params: ICellEditorParams<WbsItemAdditionalPropertyRow>) => {
      if (isGroupHeaderRow(params.data)) return
      const additionalPropertyRow = params.data as AdditionalPropertyRow
      if (
        EntitySearchReferenceEntityType.WBS_ITEM ===
        additionalPropertyRow.entitySearchReferenceEntityType
      ) {
        return Object.values(EntitySearchReferenceEntityWbsItemType).map(
          value => ({
            label: getEntitySearchEntityWbsItemTypeLabel(value),
            value,
            // In the first release, there will be a lot of tasks, so they will be excluded from the reference.
            disabled: EntitySearchReferenceEntityWbsItemType.TASK === value,
          })
        )
      } else if (
        EntitySearchReferenceEntityType.TICKET ===
        additionalPropertyRow.entitySearchReferenceEntityType
      ) {
        const ticketTypes = store.getState().project.ticketTypes
        return ticketTypes.map(v => ({
          label: v.name,
          value: v.code,
        }))
      }
      return []
    },
  },
  filter: ClientSideSelectFilter,
  floatingFilter: true,
  filterParams: {
    valueGetter: (params: { node; context }) => {
      if (!params.node || isGroupHeaderRow(params.node.data)) {
        return undefined
      }
      const additionalPropertyRow = params.node.data as AdditionalPropertyRow
      if (
        EntitySearchReferenceEntityType.WBS_ITEM ===
          additionalPropertyRow.entitySearchReferenceEntityType &&
        isEntitySearchReferenceEntityWbsItemType(
          additionalPropertyRow.entitySearchReferenceEntityWbsItemType
        )
      ) {
        return getEntitySearchEntityWbsItemTypeLabel(
          additionalPropertyRow.entitySearchReferenceEntityWbsItemType
        )
      } else if (
        EntitySearchReferenceEntityType.TICKET ===
        additionalPropertyRow.entitySearchReferenceEntityType
      ) {
        return (
          store
            .getState()
            .project.ticketTypes.find(
              t =>
                t.code ===
                additionalPropertyRow.entitySearchReferenceEntityWbsItemType
            )?.name ||
          additionalPropertyRow.entitySearchReferenceEntityWbsItemType
        )
      }
      return additionalPropertyRow.entitySearchReferenceEntityWbsItemType
    },
    getValue: o => o,
    getLabel: o => o,
  },
  cellStyle: (params: CellClassParams<WbsItemAdditionalPropertyRow>) =>
    !params.node.data ||
    isGroupHeaderRow(params.node.data) ||
    !shouldHaveEntitySearchEntityWbsItemType(
      (params.node.data as AdditionalPropertyRow).propertyType,
      (params.node.data as AdditionalPropertyRow)
        .entitySearchReferenceEntityType
    )
      ? {
          backgroundColor: DISABLE_COLOR,
        }
      : undefined,
}
